<template>
  <transition name="site-ticker-fade">
    <div
      v-if="visibleItems.length > 0"
      class="site-ticker"
      @mouseenter="pause"
      @mouseleave="resume"
    >
      <div class="site-ticker-inner">
        <div class="site-ticker-label">
          公告
        </div>
        <div class="site-ticker-wrapper">
          <div
            class="site-ticker-content"
            :class="{ paused: isPaused }"
            :style="scrollStyle"
          >
            <!-- 重复两次以实现无缝循环 -->
            <template
              v-for="(group, groupIdx) in [0, 1]"
              :key="groupIdx"
            >
              <div
                v-for="item in visibleItems"
                :key="`${item.id}-${groupIdx}`"
                class="site-ticker-item"
              >
                <span class="site-ticker-text">
                  {{ item.content }}
                </span>
                <a
                  v-if="item.linkUrl && isSafeLink(item.linkUrl)"
                  class="site-ticker-link"
                  :href="item.linkUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ item.linkText || '查看详情' }}
                </a>
                <span class="site-ticker-separator">•</span>
              </div>
            </template>
          </div>
        </div>
        <button
          v-if="visibleItems.length > 0 && visibleItems[0].dismissible !== 0 && !preview"
          class="site-ticker-close"
          type="button"
          @click="dismiss(visibleItems[0])"
        >
          ✕
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import request from '@/utils/request'

const props = defineProps({
  /**
   * 预览模式下由父组件直接传入的公告列表
   */
  items: {
    type: Array,
    default: null
  },
  /**
   * 是否为预览模式（不访问后端、也不写 localStorage）
   */
  preview: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()
const loading = ref(false)
const announcements = ref([])
const isPaused = ref(false)

const STORAGE_KEY = 'siteTickerDismissed'
const dismissedMap = ref({})

const nowMillis = () => Date.now()

const loadDismissed = () => {
  if (props.preview) return
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      dismissedMap.value = {}
      return
    }
    const list = JSON.parse(raw)
    const map = {}
    const now = nowMillis()
    if (Array.isArray(list)) {
      for (const item of list) {
        if (item && item.id && typeof item.expireAt === 'number' && item.expireAt > now) {
          map[item.id] = { expireAt: item.expireAt }
        }
      }
    }
    dismissedMap.value = map
  } catch (e) {
    console.warn('解析 siteTickerDismissed 失败', e)
    dismissedMap.value = {}
  }
}

const persistDismissed = () => {
  if (props.preview) return
  try {
    const now = nowMillis()
    const entries = Object.entries(dismissedMap.value)
      .filter(([, v]) => v && typeof v.expireAt === 'number' && v.expireAt > now)
      .map(([id, v]) => ({ id, expireAt: v.expireAt }))
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch (e) {
    console.warn('保存 siteTickerDismissed 失败', e)
  }
}

const rawItems = computed(() => {
  if (props.items && Array.isArray(props.items)) {
    return props.items
  }
  return announcements.value
})

const visibleItems = computed(() => {
  const list = rawItems.value || []
  if (props.preview) {
    return list
  }
  const now = nowMillis()
  return list.filter((item) => {
    if (!item || !item.id) return false
    const info = dismissedMap.value[item.id]
    if (!info) return true
    return !(typeof info.expireAt === 'number' && info.expireAt > now)
  })
})

// 计算滚动动画时长（根据速度 px/s，速度越大滚动越快、时长越短）
const scrollDuration = computed(() => {
  if (visibleItems.value.length === 0) return '30s'
  const avgSpeed = visibleItems.value.reduce((sum, item) => sum + (item.speed || 60), 0) / visibleItems.value.length
  // 有效速度范围 10～5000 px/s，对应时长约 120s～1.2s（60px/s ≈ 30s）
  const speed = Math.max(10, Math.min(5000, Number(avgSpeed) || 60))
  const duration = (60 / speed) * 30
  const clampedDuration = Math.max(1, Math.min(120, duration))
  return `${clampedDuration}s`
})

const scrollStyle = computed(() => {
  return {
    animationDuration: scrollDuration.value,
    animationPlayState: isPaused.value ? 'paused' : 'running'
  }
})

// 移除不需要的 clearTimer

const pause = () => {
  isPaused.value = true
}

const resume = () => {
  isPaused.value = false
}

const normalizeRoute = (r) => {
  if (!r) return '/'
  let v = r
  const q = v.indexOf('?')
  if (q >= 0) v = v.substring(0, q)
  const h = v.indexOf('#')
  if (h >= 0) v = v.substring(0, h)
  if (!v.startsWith('/')) v = `/${v}`
  return v
}

const fetchAnnouncements = async () => {
  if (props.preview) return
  loading.value = true
  try {
    const routePath = normalizeRoute(route.path)
    const res = await request.get('/announcements/active', {
      params: { route: routePath }
    })
    announcements.value = Array.isArray(res) ? res : []
  } catch (e) {
    console.error('获取站内公告失败', e)
  } finally {
    loading.value = false
  }
}

const isSafeLink = (url) => {
  if (!url) return false
  return url.startsWith('http://') || url.startsWith('https://')
}

const dismiss = (item) => {
  if (!item || !item.id || props.preview) return
  const minutes = item.dismissTtlMinutes || 60
  const ttlMs = minutes * 60 * 1000
  const expireAt = nowMillis() + ttlMs
  dismissedMap.value = {
    ...dismissedMap.value,
    [item.id]: { expireAt }
  }
  persistDismissed()
}

watch(
  () => route.path,
  () => {
    if (!props.preview) {
      fetchAnnouncements()
    }
  }
)

// 移除不需要的 watch

onMounted(() => {
  loadDismissed()
  if (!props.preview) {
    fetchAnnouncements()
  }
})

onBeforeUnmount(() => {
  // 清理工作
})
</script>

<style scoped lang="scss">
.site-ticker {
  position: sticky;
  top: 0;
  z-index: 950; // 低于 TopNav(1000)，高于内容
  margin: 0 auto var(--spacing-md);
  max-width: 1400px;
  padding: 0 var(--spacing-xl);

  @media (max-width: 768px) {
    padding: 0 var(--spacing-md);
  }
}

.site-ticker-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  border-radius: 12px;
  background: rgba(248, 249, 252, 0.96);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
  color: #111827;
  font-size: 13px;

  @media (max-width: 768px) {
    padding: 6px 10px;
    border-radius: 10px;
    font-size: 12px;
  }
}

.site-ticker-label {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(255, 255, 255, 0.9);

  @media (max-width: 768px) {
    display: none;
  }
}

.site-ticker-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 40px,
    black calc(100% - 40px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 40px,
    black calc(100% - 40px),
    transparent 100%
  );
}

.site-ticker-content {
  display: inline-flex;
  align-items: center;
  gap: 24px;
  white-space: nowrap;
  animation: ticker-scroll linear infinite;
  
  &.paused {
    animation-play-state: paused;
  }
}

.site-ticker-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.site-ticker-text {
  white-space: nowrap;
}

.site-ticker-separator {
  color: rgba(107, 114, 128, 0.4);
  margin: 0 4px;
  font-weight: 300;
}

.site-ticker-link {
  font-size: 12px;
  color: #2563eb;
  text-decoration: none;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  background: rgba(255, 255, 255, 0.9);

  &:hover {
    background: rgba(37, 99, 235, 0.06);
  }

  @media (max-width: 768px) {
    padding: 2px 6px;
    font-size: 11px;
  }
}

.site-ticker-close {
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 999px;
  font-size: 14px;

  &:hover {
    background: rgba(15, 23, 42, 0.04);
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
}

.site-ticker-fade-enter-active,
.site-ticker-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.site-ticker-fade-enter-from,
.site-ticker-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@keyframes ticker-scroll {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
}
</style>

